import { compactNumber } from "@/lib/formatNumber";
import { stripRichMarkers } from "@/lib/richFormat";
import React, { useState, useRef, useEffect } from "react";
import { emitPostPatch, usePostPatch } from "@/lib/postSync";
import { ThumbsUp, MessageCircle, Share2, Repeat2, BarChart2, WifiOff } from "lucide-react";
import { api } from "@/lib/api";
import { isOffline } from "@/lib/netStatus";
import { enqueueOffline, shouldQueue, useQueuedOffline } from "@/lib/offlineQueue";
import { requireLogin } from "@/lib/guestGate";
import { sharePostCard } from "@/lib/nativeShare";
import { usePostViewTracker } from "@/lib/postViews";
import { useApp } from "@/context/AppContext";
import { ReactorsDialog } from "@/components/ReactorsDialog";
import { ShareSheet, ShareToStorySheet } from "@/components/ShareToStory";
import { ShareToGroupSheet } from "@/components/ShareToGroupSheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { hapticLight } from "@/lib/haptics";
import { postShareLocked } from "@/lib/privacy";

// Largeur de barre sous laquelle les libellés texte disparaissent (nombres + icônes seuls, façon X).
export const NARROW_BAR_PX = 540;
// Expressive emojis for our reactions (church-specific + lively ones)
const EMOJI = { love: "👍", adore: "❤️", amen: "🙏", gloire: "🙌", sad: "😢", rire: "😂", wow: "😮" };

export const ReactionBar = ({ post, onChange, commentCount, onCommentClick, autoOpenReactors = false, dark = false, quickReactTick = 0 }) => {
  const { config, user, t, lang } = useApp();
  const reactions = config?.reactions || [];
  // `dark`: white-on-black variant for the fullscreen photo viewer (Facebook-style action row).
  const muted = dark ? "rgba(255,255,255,0.85)" : "var(--yalola-text-muted)";
  const hoverCls = dark ? "hover:bg-white/10" : "hover:bg-[color:var(--yalola-surface-alt)]";
  const lineColor = dark ? "rgba(255,255,255,0.18)" : "var(--yalola-border)";
  const [myReaction, setMyReaction] = useState(post.my_reaction || null);
  const [counts, setCounts] = useState(post.reactions || {});
  // Fresh server data (feed refresh / snapshot) → adopt it; other cards or viewers of the
  // same post (theater, fullscreen viewer, profile) → mirror their change instantly.
  useEffect(() => { setMyReaction(post.my_reaction || null); setCounts(post.reactions || {}); }, [post.id, post.my_reaction, post.reactions]);
  usePostPatch(post.id, (patch) => {
    if ("my_reaction" in patch) setMyReaction(patch.my_reaction || null);
    if (patch.reactions) setCounts(patch.reactions);
    if ("my_repost" in patch) setReposted(!!patch.my_repost);
    if (typeof patch.repost_count === "number") setRepostCount(patch.repost_count);
    if (typeof patch.views_count === "number") setViews(patch.views_count);
    if (typeof patch.shares_count === "number") setShareCount(patch.shares_count);
  });
  // Vues (📊) et partages (↗) façon X : visibles de tous, partagés entre toutes les cartes de la même publication.
  const [views, setViews] = useState(post.views_count || 0);
  const [shareCount, setShareCount] = useState(post.shares_count || 0);
  useEffect(() => { setViews(post.views_count || 0); setShareCount(post.shares_count || 0); }, [post.id, post.views_count, post.shares_count]);
  const rootRef = useRef(null);
  usePostViewTracker(rootRef, post, user);
  // Barre étroite (téléphone, panneau du théâtre PC…) → icônes + nombres seulement, jamais de libellé sur 2 lignes.
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => setNarrow(entries[0].contentRect.width < NARROW_BAR_PX));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const labelCls = narrow ? "hidden" : "inline whitespace-nowrap";
  // Republier (façon X) : état + compteur partagés entre toutes les cartes de la même publication.
  const [reposted, setReposted] = useState(!!post.my_repost);
  const [repostCount, setRepostCount] = useState(post.repost_count || 0);
  useEffect(() => { setReposted(!!post.my_repost); setRepostCount(post.repost_count || 0); }, [post.id, post.my_repost, post.repost_count]);
  const [repostersOpen, setRepostersOpen] = useState(false);
  const [repostBusy, setRepostBusy] = useState(false);
  const [unrepostOpen, setUnrepostOpen] = useState(false); // 2e tap sur ↻ → confirmation (jamais d'annulation par mégarde)
  // Profil privé (lot 522) : seul l'auteur peut republier / partager sa publication ; pour les autres, boutons masqués.
  const shareLocked = postShareLocked(post, user);
  const canRepost = !post.group && !post.is_ad && post.visibility !== "private" && !shareLocked;
  const toggleRepost = async () => {
    if (!user) { requireLogin("repost"); return; }
    if (repostBusy) return;
    setRepostBusy(true);
    try {
      const { data } = reposted ? await api.delete(`/posts/${post.id}/repost`) : await api.post(`/posts/${post.id}/repost`);
      setReposted(!!data.reposted); setRepostCount(data.repost_count || 0);
      emitPostPatch(post.id, { my_repost: !!data.reposted, repost_count: data.repost_count || 0 });
      hapticLight();
      toast.success(data.reposted ? t("post.reposted_toast", "Publication republiée — visible sur ton profil et dans le fil de tes abonnés.") : t("post.unreposted_toast", "Republication annulée"));
      onChange?.();
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setRepostBusy(false); }
  };
  const onRepostClick = () => {
    if (!user) { requireLogin("repost"); return; }
    if (reposted) setUnrepostOpen(true); else toggleRepost();
  };
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reactorsOpen, setReactorsOpen] = useState(false);
  const closeTimer = useRef(null);
  const longPressTimer = useRef(null);
  const suppressClick = useRef(false);
  const barRef = useRef(null);
  // Touch devices have no hover → tapping the main button opens the reaction picker
  // first (choose an emoji to apply), instead of instantly liking. Desktop keeps
  // hover-to-open + click-to-toggle.
  const canHover = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(hover: hover)").matches;

  // Opened straight from a "reaction" notification: show who reacted right away.
  useEffect(() => {
    if (!autoOpenReactors) return;
    const timer = setTimeout(() => setReactorsOpen(true), 400);
    return () => clearTimeout(timer);
  }, [autoOpenReactors]);

  // Mobile: close the picker when tapping anywhere outside the reaction bar.
  useEffect(() => {
    if (!pickerOpen || canHover) return undefined;
    const onDown = (e) => { if (barRef.current && !barRef.current.contains(e.target)) setPickerOpen(false); };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [pickerOpen, canHover]);

  const openPicker = () => { clearTimeout(closeTimer.current); setPickerOpen(true); };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPickerOpen(false), 350);
  };

  // Lot 599 — affichage IMMÉDIAT (optimiste) : la réaction s'affiche au tap, l'appel réseau part ensuite ; en cas
  // d'erreur on revient à l'état précédent. Avant, l'UI attendait la réponse (200-800 ms en 4G) → impression de lag.
  const react = async (key) => {
    setPickerOpen(false);
    if (!user) { requireLogin("react"); return; }
    const wasSame = myReaction === key;
    hapticLight();
    const prev = { myReaction, counts };
    const newCounts = { ...counts };
    if (wasSame) {
      newCounts[key] = Math.max(0, (newCounts[key] || 1) - 1);
    } else {
      newCounts[key] = (newCounts[key] || 0) + 1;
      if (myReaction) newCounts[myReaction] = Math.max(0, (newCounts[myReaction] || 1) - 1);
    }
    const nextReaction = wasSame ? null : key;
    setCounts(newCounts);
    setMyReaction(nextReaction);
    emitPostPatch(post.id, { my_reaction: nextReaction, reactions: newCounts });
    // Lot 630 — hors ligne : l'état reste affiché, l'envoi est mis en file et repart au retour du réseau (dernier choix gagnant).
    const queue = () => enqueueOffline({ kind: "post_reaction", key: `post:${post.id}`, method: wasSame ? "delete" : "post",
      url: `/posts/${post.id}/reactions`, body: wasSame ? null : { type: key }, meta: { post_id: post.id, type: nextReaction } });
    if (isOffline()) { queue(); return; }
    try {
      if (wasSame) await api.delete(`/posts/${post.id}/reactions`);
      else await api.post(`/posts/${post.id}/reactions`, { type: key });
    } catch (e) {
      if (shouldQueue(e)) { queue(); return; }
      setCounts(prev.counts);
      setMyReaction(prev.myReaction);
      emitPostPatch(post.id, { my_reaction: prev.myReaction, reactions: prev.counts });
      toast.error(e?.friendlyMessage || "Erreur");
    }
  };
  const queuedOffline = useQueuedOffline(`post:${post.id}`);

  // Lot 601 — double-tap sur le média (PostCard) : ❤️ « J'adore », jamais de retrait d'une réaction existante.
  useEffect(() => {
    if (!quickReactTick || myReaction) return;
    react(reactions.some((r) => r.key === "adore") ? "adore" : (reactions[0]?.key || "love"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickReactTick]);

  const mainClick = () => {
    if (suppressClick.current) { suppressClick.current = false; return; }
    if (!canHover) {
      // Touch: first tap reveals the emoji picker; a second tap (or outside tap) closes it.
      setPickerOpen((v) => !v);
      return;
    }
    react(myReaction || reactions[0]?.key || "love");
  };
  const touchStart = () => {
    longPressTimer.current = setTimeout(() => { suppressClick.current = true; openPicker(); }, 450);
  };
  const touchEnd = () => clearTimeout(longPressTimer.current);

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const current = reactions.find((r) => r.key === myReaction);
  const rl = (r) => (r ? r[`label_${lang}`] || r.label_fr || r.key : "");
  const mainLabel = current ? rl(current) : (rl(reactions[0]) || "J'aime");
  const mainColor = current ? current.color : muted;
  const nComments = commentCount || 0;

  // ↗ : membre connecté + publication publique → feuille « Ajouter à ma story / Partager ailleurs » ; sinon partage direct.
  const [shareOpen, setShareOpen] = useState(false);
  const [storyOpen, setStoryOpen] = useState(false);
  const [groupShareOpen, setGroupShareOpen] = useState(false);
  const canStoryShare = !!user && !post.group && !post.is_ad && post.visibility !== "private" && !shareLocked;
  // Lot 614 — « Partager dans un groupe » (modules groups + share_to_group).
  const canGroupShare = canStoryShare && config?.settings?.features?.share_to_group !== false && config?.settings?.features?.groups !== false;
  const share = () => { if (canStoryShare) setShareOpen(true); else shareElsewhere(); };
  const shareElsewhere = async () => {
    let text = t("post.share_default_text", "Publication Yalola");
    if (post.verse?.text) text = `« ${post.verse.text} » — ${post.verse.reference || ""}`.trim();
    else if (post.content) text = stripRichMarkers(post.content).slice(0, 140);
    else if (post.images?.length) text = t("post.share_photo_text", "📷 Photo partagée sur Yalola");
    else if (post.video_url) text = t("post.share_video_text", "🎥 Vidéo partagée sur Yalola");
    const ok = await sharePostCard({ postId: post.id, title: "Yalola", text, copiedMessage: t("post.link_copied", "Lien copié") });
    if (!ok) return; // feuille annulée → pas de partage compté
    try {
      const { data } = await api.post(`/posts/${post.id}/share`);
      if (typeof data?.shares_count === "number") { setShareCount(data.shares_count); emitPostPatch(post.id, { shares_count: data.shares_count }); }
    } catch { /* compteur seulement */ }
  };

  return (
    <div ref={rootRef} className={dark ? "mt-1" : "mt-3"} data-testid={`reaction-bar-${post.id}`} data-dark={dark ? "1" : undefined}>
      {/* Summary row: reaction emojis + total (left) · comments (right) */}
      {(total > 0 || nComments > 0 || repostCount > 0) && (
        <div className="flex items-center justify-between mb-1.5 px-0.5 text-xs" style={{ color: muted }}>
          <div className="flex items-center gap-1.5">
            {total > 0 && (
              <button
                onClick={() => setReactorsOpen(true)}
                className="flex items-center gap-1.5 hover:underline"
                data-testid={`reactors-open-${post.id}`}
              >
                <div className="flex -space-x-1">
                  {reactions.filter((r) => (counts[r.key] || 0) > 0).slice(0, 3).map((r) => (
                    <span key={r.key} className={`h-[26px] w-[26px] rounded-full grid place-items-center text-[16px] leading-none border shadow-sm ${dark ? "bg-white/15" : "bg-white"}`} style={{ borderColor: lineColor }}>
                      {EMOJI[r.key] || "👍"}
                    </span>
                  ))}
                </div>
                <span className="font-medium" data-testid={`reaction-total-${post.id}`}>{compactNumber(total)}</span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {repostCount > 0 && (
              <button onClick={() => setRepostersOpen(true)} className="hover:underline" data-testid={`repost-count-${post.id}`} data-count={repostCount}>
                {t("post.reposts_count", "{n} republication(s)").replace("{n}", compactNumber(repostCount))}
              </button>
            )}
            {nComments > 0 && (
              <button onClick={onCommentClick} className="hover:underline" data-testid={`comment-count-${post.id}`}>
                {t("post.comments_count", "{n} commentaire(s)").replace("{n}", compactNumber(nComments))}
              </button>
            )}
          </div>
        </div>
      )}

      <div ref={barRef} className="relative flex items-center border-t pt-1" style={{ borderColor: lineColor }}>
        {/* Floating reaction picker — expressive emojis, our words */}
        {pickerOpen && (
          <div
            className="reaction-picker absolute -top-[62px] left-1 right-1 sm:right-auto flex items-end gap-1 bg-white rounded-full shadow-xl border px-2.5 py-2 z-20 overflow-x-auto hide-scrollbar max-w-[calc(100vw-1.5rem)]"
            style={{ borderColor: "var(--yalola-border)" }}
            onMouseEnter={canHover ? openPicker : undefined}
            onMouseLeave={canHover ? scheduleClose : undefined}
            data-testid={`reaction-picker-${post.id}`}
          >
            {reactions.map((r) => (
              <button
                key={r.key}
                onClick={() => react(r.key)}
                title={rl(r)}
                className="flex flex-col items-center group w-10 sm:w-11 shrink-0"
                data-testid={`reaction-${r.key}-${post.id}`}
              >
                <span
                  className="text-[30px] sm:text-[34px] leading-none transition-transform duration-150 group-hover:scale-[1.25] group-hover:-translate-y-1.5 rounded-full"
                  style={{ background: myReaction === r.key ? "var(--yalola-primary-soft)" : "transparent", padding: 2 }}
                >
                  {EMOJI[r.key] || "👍"}
                </span>
                <span className="hidden sm:block text-[9px] font-bold mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap" style={{ color: r.color }}>{rl(r)}</span>
              </button>
            ))}
          </div>
        )}

        {/* Main react button: hover (desktop) or tap (mobile) opens picker, click toggles. Nombre = total des réactions (tap → qui a réagi). */}
        <div className="flex-1 flex items-center justify-center" data-testid={`react-group-${post.id}`}>
          <button
            onClick={mainClick}
            onMouseEnter={canHover ? openPicker : undefined}
            onMouseLeave={canHover ? scheduleClose : undefined}
            onTouchStart={touchStart}
            onTouchEnd={touchEnd}
            className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-[13px] font-semibold transition-colors ${hoverCls}`}
            style={{ color: mainColor }}
            data-testid={`react-main-${post.id}`}
          >
            {current ? (
              <span className="text-[20px] leading-none">{EMOJI[current.key] || "👍"}</span>
            ) : (
              <ThumbsUp size={19} />
            )}
            <span className={labelCls}>{mainLabel}</span>
            {queuedOffline && <WifiOff size={12} className="shrink-0 opacity-70" title={t("offline.waiting", "En attente du réseau")} aria-label={t("offline.waiting", "En attente du réseau")} data-testid={`react-offline-${post.id}`} />}
          </button>
          {total > 0 ? (
            <button
              onClick={() => setReactorsOpen(true)}
              className={`-ml-1 py-2 pr-2 pl-0.5 rounded-lg text-[13px] font-semibold tabular-nums transition-colors ${hoverCls}`}
              style={{ color: mainColor }}
              aria-label={t("post.reactions_who_n", "{n} réaction(s)").replace("{n}", compactNumber(total))}
              data-testid={`react-num-${post.id}`}
              data-count={total}
            >
              {compactNumber(total)}
            </button>
          ) : (
            <span className="-ml-1 py-2 pr-2 pl-0.5 text-[13px] font-semibold tabular-nums" style={{ color: muted }} data-testid={`react-num-${post.id}`} data-count={0}>0</span>
          )}
        </div>
        {/* Commenter : icône + nombre toujours visibles (façon X), libellé seulement sur barre large. */}
        <button
          onClick={onCommentClick}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[13px] font-semibold transition-colors ${hoverCls}`}
          style={{ color: muted }}
          aria-label={t("post.comments_count", "{n} commentaire(s)").replace("{n}", compactNumber(nComments))}
          data-testid={`comment-toggle-${post.id}`}
          data-count={nComments}
        >
          <MessageCircle size={17} />
          <span className={labelCls}>{t("post.comment_action", "Commenter")}</span>
          <span className="tabular-nums" data-testid={`comment-num-${post.id}`}>{compactNumber(nComments)}</span>
        </button>
        {canRepost && (
          <div className="flex-1 flex items-center justify-center" data-testid={`repost-group-${post.id}`}>
            <button
              onClick={onRepostClick}
              disabled={repostBusy}
              className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-[13px] font-semibold transition-colors ${hoverCls}`}
              style={{ color: reposted ? "var(--yalola-secondary)" : muted }}
              aria-pressed={reposted}
              data-testid={`repost-btn-${post.id}`}
              data-reposted={reposted ? "1" : "0"}
            >
              <Repeat2 size={18} strokeWidth={reposted ? 2.6 : 2} />
              <span className={labelCls}>{reposted ? t("post.you_reposted", "Tu as republié") : t("post.repost", "Republier")}</span>
            </button>
            {repostCount > 0 ? (
              // Compteur façon X : cible de tap DISTINCTE de l'icône → liste « Republié par N » (jamais de republication par erreur).
              <button
                onClick={() => setRepostersOpen(true)}
                className={`-ml-1 py-2 pr-2 pl-0.5 rounded-lg text-[13px] font-semibold tabular-nums transition-colors ${hoverCls}`}
                style={{ color: reposted ? "var(--yalola-secondary)" : muted }}
                aria-label={t("post.reposts_who_n", "Republié par {n}").replace("{n}", compactNumber(repostCount))}
                data-testid={`repost-num-${post.id}`}
                data-count={repostCount}
              >
                {compactNumber(repostCount)}
              </button>
            ) : (
              <span className="-ml-1 py-2 pr-2 pl-0.5 text-[13px] font-semibold tabular-nums" style={{ color: muted }} data-testid={`repost-num-${post.id}`} data-count={0}>0</span>
            )}
          </div>
        )}
        {post.group?.privacy !== "private" && !shareLocked && (
          <div className="flex-1 flex items-center justify-center" data-testid={`share-group-${post.id}`}>
            <button
              onClick={share}
              className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-[13px] font-semibold transition-colors ${hoverCls}`}
              style={{ color: muted }}
              data-testid={`share-btn-${post.id}`}
            >
              <Share2 size={17} />
              <span className={labelCls}>{t("post.share_action", "Partager")}</span>
            </button>
            {/* Compteur ↗ façon X, toujours affiché (partages réussis : feuille native, Web Share ou lien copié). */}
            <span
              className="-ml-1 py-2 pr-2 pl-0.5 text-[13px] font-semibold tabular-nums"
              style={{ color: muted }}
              aria-label={t("post.shares_n", "{n} partage(s)").replace("{n}", compactNumber(shareCount))}
              data-testid={`share-count-${post.id}`}
              data-count={shareCount}
            >
              {compactNumber(shareCount)}
            </span>
          </div>
        )}
        {/* Vues façon X : icône 📊 + nombre, en fin de barre, visibles de tous (anonymes inclus). */}
        <div
          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-2 text-[13px] font-semibold tabular-nums"
          style={{ color: muted }}
          title={t("post.views_n", "{n} vue(s)").replace("{n}", compactNumber(views))}
          aria-label={t("post.views_n", "{n} vue(s)").replace("{n}", compactNumber(views))}
          data-testid={`views-${post.id}`}
          data-count={views}
        >
          <BarChart2 size={17} />
          <span>{compactNumber(views)}</span>
        </div>
      </div>

      <AlertDialog open={unrepostOpen} onOpenChange={setUnrepostOpen}>
        <AlertDialogContent className="z-[130] w-[calc(100%-2rem)] rounded-xl" overlayClassName="z-[130]" data-testid={`unrepost-confirm-${post.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("post.unrepost_confirm", "Annuler la republication ?")}</AlertDialogTitle>
            <AlertDialogDescription>{t("post.unrepost_confirm_desc", "Elle disparaîtra de ton profil et du fil de tes abonnés. Tu pourras republier à tout moment.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`unrepost-keep-${post.id}`}>{t("post.unrepost_keep", "Garder")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setUnrepostOpen(false); toggleRepost(); }} data-testid={`unrepost-do-${post.id}`}>{t("post.unrepost_do", "Retirer")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {canStoryShare && (
        <>
          <ShareSheet open={shareOpen} onOpenChange={setShareOpen} testId={`share-sheet-${post.id}`}
            onStory={() => { setShareOpen(false); setStoryOpen(true); }}
            onGroup={canGroupShare ? () => { setShareOpen(false); setGroupShareOpen(true); } : undefined}
            onOther={() => { setShareOpen(false); shareElsewhere(); }} />
          <ShareToStorySheet post={post} open={storyOpen} onOpenChange={setStoryOpen}
            onPublished={(n) => { setShareCount(n); emitPostPatch(post.id, { shares_count: n }); }} />
          {canGroupShare && (
            <ShareToGroupSheet post={post} open={groupShareOpen} onOpenChange={setGroupShareOpen}
              onShared={(n) => { setShareCount(n); emitPostPatch(post.id, { shares_count: n }); }} />
          )}
        </>
      )}
      <ReactorsDialog
        open={reactorsOpen}
        onOpenChange={setReactorsOpen}
        endpoint={`/posts/${post.id}/reactions`}
        testId={`reactors-dialog-${post.id}`}
      />
      <ReactorsDialog
        open={repostersOpen}
        onOpenChange={setRepostersOpen}
        endpoint={`/posts/${post.id}/reposts`}
        testId={`reposters-dialog-${post.id}`}
        title={t("post.reposts_who_n", "Republié par {n}")}
        emptyLabel={t("post.reposts_empty", "Personne n'a encore republié")}
      />
    </div>
  );
};
